import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';

import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledTMPackageInfoSection = styled.div`
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: ${spaces.large6X + 2}px;

  background-color: ${colors.white};
  box-shadow: 0px 10px 80px rgba(0, 0, 0, 0.05);
  border-radius: 40px;

  @media ${devices.tablet} {
    padding: ${spaces.regular}px;
    background-color: unset;
    box-shadow: none;
  }
`;

const StyledTitle = styled.div`
  margin-bottom: ${spaces.large6X}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large11X}px;
  line-height: 150%;
  text-align: center;
  color: ${colors.gray[800]};

  @media ${devices.tablet} {
    margin-bottom: ${spaces.medium}px;
    font-size: ${fontSizes.large2X}px;
  }
`;

const StyledBody = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: ${spaces.largeX + 2}px;
  width: 100%;

  @media ${devices.tablet} {
    gap: ${spaces.regular}px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCard = styled(forwardRef(({ active, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spaces.medium}px;

  width: ${`calc((100% - ${(spaces.largeX + 2) * 3}px) / 4)`};

  padding: ${spaces.medium}px;

  background-color: ${colors.gray[50]};
  border-radius: 16px;
  color: ${colors.gray[800]};

  cursor: pointer;

  &:hover {
    background-color: ${colors.indigo[50]};
    color: ${colors.indigo[600]};

    svg {
      path {
        fill: ${colors.indigo[600]};
      }
    }
  }

  @media ${devices.tablet} {
    align-items: center;
    width: ${`calc((100% - ${spaces.regular * 2}px) / 2)`};
    padding: ${spaces.small}px;
    border-radius: 16px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCardIcon = styled(forwardRef(({ desktop, mobile, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  justify-content: center;

  min-height: 50px;
  min-width: 50px;

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;

const StyledCardTitle = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large}px;
  text-align: left;

  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
    text-align: center;
  }
`;

const TMPackageInfoSection = ({ items = [] }) => (
  <StyledTMPackageInfoSection>
    <StyledTitle>Whats included in our package</StyledTitle>
    <StyledBody>
      {items?.map(({ key, title, iconAlias, iconColor }) => (
        <StyledCard key={key}>
          <StyledCardIcon>
            <Icon alias={iconAlias} color={iconColor} height={50} width={50} />
          </StyledCardIcon>
          <StyledCardTitle>{title}</StyledCardTitle>
        </StyledCard>
      ))}
    </StyledBody>
  </StyledTMPackageInfoSection>
);

TMPackageInfoSection.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.node,
      iconAlias: PropTypes.string,
      iconColor: PropTypes.string,
    })
  ),
};

export default TMPackageInfoSection;
